import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function RealizationsCard({ currency, refreshKey = 0 }) {
  const [data, setData] = useState({ realizations: [], total_realized_pnl: 0, total_proceeds: 0, total_cost_basis: 0 });
  const [loading, setLoading] = useState(true);
  const currentYear = new Date().getFullYear();
  const [exportYear, setExportYear] = useState(currentYear);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetch(`${API}/investments/realizations?currency=${currency}`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(j => { if (alive && j) setData(j); })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [currency, refreshKey]);

  const fmt = (n) => `${currency} ${Number(n || 0).toFixed(2)}`;
  const pnlClass = (v) => (v >= 0 ? 'text-success' : 'text-destructive');

  const handleExport = async (format) => {
    setExporting(true);
    try {
      const url = `${API}/investments/realizations/export?year=${exportYear}&format=${format}&currency=${currency}`;
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) {
        toast.error('Export failed');
        return;
      }
      const blob = await res.blob();
      const a = document.createElement('a');
      const blobUrl = URL.createObjectURL(blob);
      a.href = blobUrl;
      a.download = `capital_gains_${exportYear}_${currency}.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
      toast.success(`Exported ${format.toUpperCase()}`);
    } catch {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  // Build year options: current year + 4 prior years
  const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - i);

  return (
    <Card data-testid="realizations-card" className="border border-border mb-8">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <CardTitle className="text-xl sm:text-2xl tracking-tight font-semibold">
              Capital Gains Log <span className="text-xs font-normal text-muted-foreground tracking-wider uppercase ml-2">FIFO</span>
            </CardTitle>
            <CardDescription>
              Every sell, with the cost basis of the FIFO-consumed lots. Useful for tax reporting and reviewing trade quality.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <label htmlFor="export-year" className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Year</label>
            <select
              id="export-year"
              data-testid="export-year-select"
              value={exportYear}
              onChange={(e) => setExportYear(parseInt(e.target.value, 10))}
              className="border border-border rounded-md px-2 py-1 text-sm bg-background"
            >
              {yearOptions.map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
            <Button
              data-testid="export-csv-btn"
              variant="outline"
              size="sm"
              onClick={() => handleExport('csv')}
              disabled={exporting}
            >
              <Download className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} />
              CSV
            </Button>
            <Button
              data-testid="export-pdf-btn"
              variant="outline"
              size="sm"
              onClick={() => handleExport('pdf')}
              disabled={exporting}
            >
              <Download className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} />
              PDF
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0 sm:p-6 sm:pt-0">
        {loading ? (
          <div className="p-6 text-center text-sm text-muted-foreground">Loading…</div>
        ) : data.realizations.length === 0 ? (
          <div className="p-6 text-center py-12 text-muted-foreground text-sm">
            No realized gains yet. Sell some positions to see capital-gain entries here.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-4 px-6 pt-2 pb-4 border-b border-border">
              <div>
                <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Total Proceeds</div>
                <div data-testid="realizations-total-proceeds" className="text-lg font-semibold tabular-nums">{fmt(data.total_proceeds)}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Total Cost Basis</div>
                <div data-testid="realizations-total-cost" className="text-lg font-semibold tabular-nums">{fmt(data.total_cost_basis)}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Total Realized P&L</div>
                <div data-testid="realizations-total-pnl" className={`text-lg font-semibold tabular-nums ${pnlClass(data.total_realized_pnl)}`}>
                  {data.total_realized_pnl >= 0 ? '+' : ''}{fmt(data.total_realized_pnl)}
                </div>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border">
                  <tr className="text-left text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">
                    <th className="py-3 px-4">Date</th>
                    <th className="py-3 px-4">Symbol</th>
                    <th className="py-3 px-4 text-right">Qty</th>
                    <th className="py-3 px-4 text-right">Sell Price</th>
                    <th className="py-3 px-4 text-right">Proceeds</th>
                    <th className="py-3 px-4 text-right">Cost Basis</th>
                    <th className="py-3 px-4 text-right">Realized P&L</th>
                    <th className="py-3 px-4 text-right">Lots Used</th>
                  </tr>
                </thead>
                <tbody>
                  {data.realizations.map((r) => (
                    <tr key={r.realization_id} data-testid={`realization-${r.realization_id}`} className="border-b border-border">
                      <td className="py-3 px-4 tabular-nums">{r.date}</td>
                      <td className="py-3 px-4 font-semibold">{r.symbol}</td>
                      <td className="py-3 px-4 text-right tabular-nums">{r.quantity}</td>
                      <td className="py-3 px-4 text-right tabular-nums">{fmt(r.sell_price)}</td>
                      <td className="py-3 px-4 text-right tabular-nums">{fmt(r.proceeds)}</td>
                      <td className="py-3 px-4 text-right tabular-nums">{fmt(r.cost_basis)}</td>
                      <td className={`py-3 px-4 text-right tabular-nums font-semibold ${pnlClass(r.realized_pnl)}`}>
                        {r.realized_pnl >= 0 ? '+' : ''}{fmt(r.realized_pnl)}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <Badge variant="outline" className="text-xs font-normal">{r.lots_consumed?.length || 0}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default RealizationsCard;
